import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import {
  Route,
  Routes,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import ContentLoader from "react-content-loader";

import useWorks from "../../hooks/useWorks";
import useUsers from "../../hooks/useUsers";
import useJournals from "../../hooks/useJournals";

import AppContext from "../../AppContext";
import ShoppingContext from "../../contexts/ShoppingContext";
import {
  PAGE_STATE,
  PROGRAM_NAME_VALUE_MAPPING,
  RENDERER_NAME_VALUE_MAPPING,
} from "../../constants";
import { CartContext } from "../../contexts/CartContext";
import {
  langComponent,
  PROGRAM_MAPPING,
  RENDERER_MAPPING,
} from "../../constants";
import { ReactComponent as EmptySvg } from "../../assets/icons/empty-projects.svg";

import SearchBox from "../common/SearchBox";

import { ReactComponent as FilterDesktopSvg } from "../../assets/icons/filter-primary.svg";
import { ReactComponent as SearchSvg } from "../../assets/header/search-primarycolor.svg";
import { ReactComponent as LeftArrowSvg } from "../../assets/header/left-arrow.svg";
import { ReactComponent as RemoveSvg } from "../../assets/filters/remove.svg";

//component
import PaginationNew from "../Pagination/PaginationNew";
import Card from "./Card";
import Header from "./Header";
import { ChoiceChip } from "../common/ChoiceChip";

//style
import "../../styles/workExchangeCategory.scss";
import Filter from "./Filter";
import ComboBox from "../common/ComboBox";
import _ from "lodash";

const TABS = {
  All: 1,
  Free: 2,
  NewWork: 3,
  Discount: 4,
};

function Category() {
  const { id: workExchangeType } = useParams();
  const { designStyles, user, getLang } = useContext(AppContext);
  const { shoppingFilter, setShoppingFilter } = useContext(ShoppingContext);
  const { isFetchDetail, setIsFetchDetail, items } = useContext(CartContext);
  const navigate = useNavigate();
  const { queryWorks } = useWorks();
  const { queryItems, queryUsers, queryCompanies } = useUsers();
  const { queryJournals } = useJournals();
  const TranslationCommon = getLang(langComponent.common);
  const TranslationWorkExchange = getLang(langComponent.workExchange);
  const [searchText, setSearchText] = useState(undefined);
  const [selectedTab, setSelectedTab] = useState(1);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [projects, setProjects] = useState(undefined);
  const [metadata, setMetadata] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [fetchInit, setFetchInit] = useState(false);
  const [shoppingItem, setShoppingItem] = useState([]);
  const [journals, setJournals] = useState([]);
  const [isToggleFilter, setIsToggleFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [isToggleMobileSearch, setIsToggleMobileSearch] = useState(false);

  const _quickFilters = [
    {
      name: TranslationCommon("All"),
      value: TABS.All,
    },
    {
      name: TranslationWorkExchange("Free"),
      value: TABS.Free,
    },
    {
      name: TranslationWorkExchange("New work"),
      value: TABS.NewWork,
    },
    {
      name: TranslationWorkExchange("On Sale"),
      value: TABS.Discount,
    },
  ];

  const _availableFilters = useMemo(() => {
    if (!designStyles.workExchanges)
      return {
        categories: [],
        subCategories: [],
        materials: [],
        fileTypes: [],
      };

    const categories = [];
    const subCategories = [];
    const materials = [];
    const fileTypes = [];

    for (const _type of designStyles.workExchanges) {
      for (const ft of _type.fileTypes) {
        if (fileTypes.find((f) => f.name === ft)) {
          continue;
        }
        fileTypes.push({
          name: ft,
          value: ft,
        });
      }

      for (const _cat of _type.categories) {
        categories.push({
          name: `${_cat.name}${_cat.projects ? ` (${_cat.projects})` : ""}`,
          value: _cat._id,
        });

        for (const subcat of _cat.subs) {
          subCategories.push({
            name: `${subcat.name}${
              subcat.projects ? ` (${subcat.projects})` : ""
            }`,
            value: subcat._id,
          });

          for (const m of subcat.materials) {
            materials.push({
              name: `${m.name}${m.projects ? ` (${m.projects})` : ""}`,
              value: m._id,
            });
          }
        }
      }
    }

    return {
      categories,
      subCategories,
      materials,
      fileTypes,
    };
  }, [designStyles.workExchanges]);

  const handleTabClick = (tabValue) => {
    if (tabValue === TABS.All && selectedTab === TABS.All) {
      return;
    }

    if (selectedTab === tabValue) {
      setSelectedTab(null);
      return;
    }

    setSelectedTab(tabValue);
  };

  useEffect(() => {
    if (!selectedTab) {
      // * Reset to Tabs to all
      setSelectedTab(TABS.All);
      return;
    }

    if (fetchInit === false) {
      _init();
    }

    setFetchInit(false);
    setLoading(true);
    setPageState(PAGE_STATE.IDLE);
  }, [shoppingFilter, searchText, selectedTab, user?._id, items]);

  const _init = async () => {
    setIsFetchDetail(false);

    const { categories, subs, programs, renderers, types, sort } =
      shoppingFilter || {};

    // * Construct filter
    const filterArray = [];

    if (workExchangeType) {
      filterArray.push({
        "designType.workExchange": { $eq: workExchangeType },
      });
    }

    if (categories) {
      filterArray.push({
        "designType.workExchangeCategory": { $in: categories },
      });
    }

    if (subs) {
      filterArray.push({
        "designType.workExchangeSubCategory": { $in: subs },
      });
    }

    if (selectedTab == TABS.Free) {
      filterArray.push({
        price: 0,
      });
    }

    if (selectedTab == TABS.NewWork) {
      let currentDate = new Date();
      let newWork = new Date(currentDate.setHours(17, 0, 0, 0));
      newWork.setDate(currentDate.getDate() - 7);

      filterArray.push({
        createdOn: {
          $gte: newWork,
        },
      });
    }

    if (selectedTab == TABS.Discount) {
      // * Check discount
      filterArray.push({
        discount: {
          $gt: 0,
        },
      });
    }

    if (programs) {
      filterArray.push({
        program: { $in: programs.map((_) => PROGRAM_MAPPING[_]) },
      });
    }

    if (renderers) {
      filterArray.push({
        renderer: { $in: renderers.map((_) => RENDERER_MAPPING[_]) },
      });
    }

    if (types) {
      filterArray.push({ workFileTypes: { $in: types } });
    }

    if (searchText) {
      filterArray.push({ name: { $regex: searchText } });
    }

    filterArray.push({
      active: true,
      deleted: false,
    });

    let filter = {};

    if (filterArray.length !== 0) {
      filter = {
        $and: filterArray,
      };
    }

    const { data: _project, _metadata } = await queryWorks(
      filter,
      page || 1,
      limit || metadata?.pageSize || 12,
      {
        name: 1,
        createdBy: 1,
        desc: 1,
        brief: 1,
        price: 1,
        banner: 1,
        program: 1,
        renderer: 1,
        views: 1,
        designType: 1,
        createdOn: 1,
        discount: 1,
        company: 1,
      },
      sort ? sort : { createdOn: -1 }
    );

    // * api query Shopping item
    if (user?._id) {
      const { data: _shoppingItems } = await queryItems(user._id);
      setShoppingItem(_shoppingItems);
    }

    // * api query users
    const { data: _users } = await queryUsers(
      { _id: { $in: _project.map((d) => d.company ?? d.createdBy) } },
      1,
      _project.length,
      { firstname: 1, surname: 1, avatar: 1 }
    );

    const { data: companies } = await queryCompanies(
      {
        _id: {
          $in: _project.map((d) => d.company ?? d.createdBy),
        },
      },
      null,
      -1,
      {
        _id: 1,
        name: 1,
        logo: 1,
      }
    );

    if (_project.length == 0) {
      setProjects([]);
    } else {
      setProjects(
        _project.map((w) => {
          const userFound = _users.find((u) => u._id === w.createdBy);
          const _company = companies.find(
            (company) => w?.company === company._id
          );

          return {
            ...w,
            user: _company
              ? {
                  name: _company.name,
                  avatar: _company.logo,
                }
              : {
                  name: `${userFound.firstname} ${userFound.surname}`,
                  avatar: userFound.avatar,
                },
          };
        })
      );
    }

    const _workIdList = _project.map((item) => item._id);

    if (user?._id) {
      const { data: _journals } = await queryJournals({
        "details.work": {
          $in: _workIdList,
        },
        company: user?.company, // * Company
        createdBy: user?._id, // * User ที่ login
        active: true,
        deleted: false,
        status: 2,
      });
      setJournals(_journals);
    }
    setIsFetchDetail(true);
    setMetadata(_metadata);
    setLoading(true);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleOnPageChange = (page) => {
    setLoading(true);
    setPageState(PAGE_STATE.LOADING);
    _init(page);
    setFetchInit(true);
    setIsFetchDetail(true);
  };

  const handleOnLimitChange = async (limit) => {
    _init(undefined, limit);
  };

  const handleClickView = () => {
    navigate(`/work-exchanges/${_id}`);
  };

  const handleSearchChanged = (value) => {
    setSearchText(value);
  };

  const getActiveFilters = () => {
    const { categories, subs, types, programs, renderers } = shoppingFilter;

    const _active = [];
    let _remaining = 0;

    for (const cat of categories ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {_availableFilters.categories.find((_) => _.value === cat).name}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              const _newShoppingFilter = _.cloneDeep(shoppingFilter);

              const _categories =
                _newShoppingFilter.categories?.filter((_) => _ !== cat) ?? [];

              if (_.isEmpty(_categories)) {
                delete _newShoppingFilter.categories;
                setShoppingFilter(_newShoppingFilter);
                return;
              }

              _newShoppingFilter.categories = _categories;
              setShoppingFilter(_newShoppingFilter);
            }}
          />
        </div>
      );
    }

    for (const sub of subs ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {_availableFilters.subCategories.find((_) => _.value === sub).name}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              const _newShoppingFilter = _.cloneDeep(shoppingFilter);

              const _subCategories =
                _newShoppingFilter.subs?.filter((_) => _ !== sub) ?? [];

              if (_.isEmpty(_subCategories)) {
                delete _newShoppingFilter.subs;
                setShoppingFilter(_newShoppingFilter);
                return;
              }

              _newShoppingFilter.subs = _subCategories;
              setShoppingFilter(_newShoppingFilter);
            }}
          />
        </div>
      );
    }

    for (const type of types ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {type}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              const _newShoppingFilter = _.cloneDeep(shoppingFilter);

              const _types =
                _newShoppingFilter.types?.filter((_) => _ !== type) ?? [];

              if (_.isEmpty(_types)) {
                delete _newShoppingFilter.types;
                setShoppingFilter(_newShoppingFilter);
                return;
              }

              _newShoppingFilter.types = _types;
              setShoppingFilter(_newShoppingFilter);
            }}
          />
        </div>
      );
    }

    for (const program of programs ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {PROGRAM_NAME_VALUE_MAPPING[program]}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              const _newShoppingFilter = _.cloneDeep(shoppingFilter);

              const _programs =
                _newShoppingFilter.programs?.filter((_) => _ !== program) ?? [];

              if (_.isEmpty(_programs)) {
                delete _newShoppingFilter.programs;
                setShoppingFilter(_newShoppingFilter);
                return;
              }

              _newShoppingFilter.programs = _programs;
              setShoppingFilter(_newShoppingFilter);
            }}
          />
        </div>
      );
    }

    for (const renderer of renderers ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {RENDERER_NAME_VALUE_MAPPING[renderer]}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              const _newShoppingFilter = _.cloneDeep(shoppingFilter);

              const _renderers =
                _newShoppingFilter.renderers?.filter((_) => _ !== renderer) ??
                [];

              if (_.isEmpty(_renderers)) {
                delete _newShoppingFilter.renderers;
                setShoppingFilter(_newShoppingFilter);
                return;
              }

              _newShoppingFilter.renderers = _renderers;
              setShoppingFilter(_newShoppingFilter);
            }}
          />
        </div>
      );
    }

    if (_remaining > 0) {
      _active.unshift(
        <div
          className="shopping-container-active-filters-item"
          style={{ padding: "2px 8px", cursor: "pointer" }}
          onClick={() => setIsToggleFilter(true)}
        >
          <div className="shopping-container-active-filters-item__value">
            + {_remaining}
          </div>
        </div>
      );
    }

    if (_active.length === 0) {
      return <></>;
    }

    return <div className="shopping-container-active-filters">{_active}</div>;
  };

  if (!projects) return;

  return (
    <>
      <div className="category-container">
        <Header
          haveTab={false}
          id={workExchangeType}
          shoppingItem={shoppingItem}
        />
        <div className="category-container-section">
          <div className="category-container-section-list">
            {!isToggleMobileSearch && (
              <div className="category-container-section-list-filters">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="category-container-section-list-quick-tabs">
                    {_quickFilters.map((tab) => {
                      const isActive = selectedTab === tab.value;

                      return (
                        <div
                          className="category-container-section-list-quick-tabs__item"
                          key={tab.value}
                        >
                          <ChoiceChip
                            label={tab.name}
                            onClick={() => handleTabClick(tab.value)}
                            isSelected={isActive}
                            width={80}
                            height={24}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="category-container-section-list-quick-filter">
                    <ComboBox
                      options={_quickFilters}
                      defaultValue={selectedTab}
                      width={150}
                      onChange={({ value }) => {
                        handleTabClick(value);
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="category-container-section-list-filters__mobile-search">
                    <SearchSvg
                      width={26}
                      onClick={() => {
                        setIsToggleMobileSearch((curr) => !curr);
                      }}
                    />
                  </div>
                  <div className="category-container-section-list-filters__search">
                    <SearchBox name="search" onChange={handleSearchChanged} />
                  </div>
                  <span
                    className="category-container-section-list-filters__advanced"
                    onClick={() => setIsToggleFilter(true)}
                  >
                    <FilterDesktopSvg width={28} />
                    Filters
                  </span>
                </div>
              </div>
            )}
            {isToggleMobileSearch && (
              <div className="category-container-section-list-mobile-search">
                <LeftArrowSvg
                  width={18}
                  onClick={() => setIsToggleMobileSearch((curr) => !curr)}
                />
                <div style={{ flexGrow: 1 }}>
                  <SearchBox name="search" onChange={handleSearchChanged} />
                </div>
              </div>
            )}
            {shoppingFilter && getActiveFilters()}
            <div>
              {loading ? (
                <div className="category-container-section-list-card">
                  {projects.length > 0 ? (
                    <>
                      {projects.map((project, index) => (
                        <Card
                          key={index}
                          {...project}
                          creator={project.user}
                          workJournals={journals}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="category-container-section-list-notfound">
                      <EmptySvg height={224} style={{ opacity: 0.3 }} />
                      <span
                        style={{
                          fontSize: 16,
                          color: "#9d9d9d",
                          marginTop: -8,
                        }}
                      >
                        No results found
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="category-container-section-list-loader">
                  {projects.map((project, index) => {
                    return (
                      <div key={index}>
                        <ContentLoader viewBox="0 0 300 400">
                          <rect
                            x="0"
                            y="0"
                            rx={20}
                            ry={20}
                            width="300"
                            height="100%"
                          />
                        </ContentLoader>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="category-container-section-list-pagination">
              <PaginationNew
                defaultLimit={metadata?.pageSize || 12}
                defaultPage={metadata?.currentPage || 1}
                pages={metadata?.totalPages || 1}
                onPageChange={handleOnPageChange}
                onLimitChange={handleOnLimitChange}
              />
            </div>
          </div>
        </div>
      </div>
      {isToggleFilter && (
        <Filter
          workExchangeType={
            designStyles.workExchanges.find(
              (we) => we.value === workExchangeType
            )?.name
          }
          onClose={() => setIsToggleFilter(false)}
        />
      )}
    </>
  );
}

Category.propTypes = {};

export default Category;
